import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface Profile {
  display_name: string;
  created_at: string;
  gdpr_consent_at: string;
  is_premium: boolean;
  premium_until: string | null;
  subscription_tier: string;
  avatar_url: string | null;
  default_avatar: number | null;
}

export type SubscriptionTier = 'free' | 'basic' | 'premium';

interface UserWithRoles {
  id: string;
  email: string;
  displayName: string;
  createdAt?: string;
  gdprConsentAt?: string;
  roles: string[];
  isPremium: boolean;
  premiumUntil: string | null;
  subscriptionTier: SubscriptionTier;
  avatarUrl: string | null;
  defaultAvatar: number | null;
}

interface AuthContextValue {
  user: UserWithRoles | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  canModerate: boolean;
  isPremium: boolean;
  isFullPremium: boolean;
  subscriptionTier: SubscriptionTier;
  profile: {
    display_name: string;
    created_at: string | undefined;
    gdpr_consent_at: string | undefined;
    avatar_url: string | null;
    default_avatar: number | null;
  } | null;
  resendConfirmation: (email: string) => Promise<{ error: string | null }>;
  register: (email: string, password: string, displayName: string) => Promise<{ error: string | null }>;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  updateProfile: (displayName: string) => Promise<{ error: string | null }>;
  deleteAccount: () => Promise<{ error: string | null }>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null; message?: string }>;
  resetPassword: (newPassword: string) => Promise<{ error: string | null; message?: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (authUser: User) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, created_at, gdpr_consent_at, is_premium, premium_until, subscription_tier, avatar_url, default_avatar')
        .eq('id', authUser.id)
        .single();

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authUser.id);

      const userRoles = roles?.map(r => r.role) || [];

      setUser({
        id: authUser.id,
        email: authUser.email || '',
        displayName: profile?.display_name || 'Användare',
        createdAt: profile?.created_at,
        gdprConsentAt: profile?.gdpr_consent_at,
        roles: userRoles,
        isPremium: profile?.is_premium || false,
        premiumUntil: profile?.premium_until,
        subscriptionTier: (profile?.subscription_tier as SubscriptionTier) || 'free',
        avatarUrl: profile?.avatar_url || null,
        defaultAvatar: profile?.default_avatar ?? null,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, []);

  const wasLoggedIn = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth event:', event);
        
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }
        
        if (event === 'TOKEN_REFRESHED' && !session) {
          if (wasLoggedIn.current) {
            toast.error('Din session har löpt ut. Logga in igen.');
            window.location.href = '/auth';
          }
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        if (session?.user) {
          wasLoggedIn.current = true;
          setTimeout(() => fetchUserData(session.user), 0);
        } else {
          if (wasLoggedIn.current) {
            toast.error('Din session har löpt ut. Logga in igen.');
            wasLoggedIn.current = false;
            window.location.href = '/auth';
          }
          setUser(null);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        wasLoggedIn.current = true;
        fetchUserData(session.user);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  // Proactive session check every 5 minutes
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if ((error || !currentSession) && wasLoggedIn.current) {
        console.log('Session check failed, logging out');
        wasLoggedIn.current = false;
        setUser(null);
        setSession(null);
        toast.error('Din session har löpt ut. Logga in igen.');
        window.location.href = '/auth';
      }
    };
    
    if (user) {
      const intervalId = setInterval(checkSession, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  const register = async (email: string, password: string, displayName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { display_name: displayName },
        },
      });
      if (error) {
        if (error.message.includes('already registered')) {
          return { error: 'E-postadressen är redan registrerad' };
        }
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte registrera. Försök igen.';
      return { error: message };
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = true) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          return { error: 'Felaktig e-post eller lösenord' };
        }
        return { error: error.message };
      }
      if (!rememberMe && data.session) {
        localStorage.removeItem('sb-swnfkzundjnhijytuwxi-auth-token');
        sessionStorage.setItem('sb-swnfkzundjnhijytuwxi-auth-token', JSON.stringify(data.session));
      }
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte logga in. Försök igen.';
      return { error: message };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('Sign out error (ignored):', e);
    }
    setUser(null);
    setSession(null);
    localStorage.removeItem('sb-swnfkzundjnhijytuwxi-auth-token');
    sessionStorage.removeItem('sb-swnfkzundjnhijytuwxi-auth-token');
  };

  const updateProfile = async (displayName: string) => {
    if (!session?.user) return { error: 'Ej inloggad' };
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName })
        .eq('id', session.user.id);
      if (error) throw error;
      setUser(prev => prev ? { ...prev, displayName } : null);
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte uppdatera profil';
      return { error: message };
    }
  };

  const deleteAccount = async () => {
    if (!session?.user) return { error: 'Ej inloggad' };
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', session.user.id);
      if (error) throw error;
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte radera konto';
      return { error: message };
    }
  };

  const resendConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte skicka om bekräftelsemail';
      return { error: message };
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });
      if (error) throw error;
      return { error: null, message: 'Återställningslänk skickad till din e-post' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte skicka återställningslänk';
      return { error: message };
    }
  };

  const resetPassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { error: null, message: 'Lösenord uppdaterat' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte återställa lösenord';
      return { error: message };
    }
  };

  const isAdmin = user?.roles?.includes('admin') ?? false;
  const isModerator = user?.roles?.includes('moderator') ?? false;
  const canModerate = isAdmin || isModerator;
  const subscriptionTier = user?.subscriptionTier || 'free';
  const isPremium = user?.isPremium || isAdmin;
  const isFullPremium = subscriptionTier === 'premium' || isAdmin;

  const value: AuthContextValue = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isModerator,
    canModerate,
    isPremium,
    isFullPremium,
    subscriptionTier,
    profile: user ? {
      display_name: user.displayName,
      created_at: user.createdAt,
      gdpr_consent_at: user.gdprConsentAt,
      avatar_url: user.avatarUrl,
      default_avatar: user.defaultAvatar,
    } : null,
    resendConfirmation,
    register,
    login,
    signOut,
    updateProfile,
    deleteAccount,
    requestPasswordReset,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
